import { idbGet, idbSet } from './idb';
/**
 * Player profile state — streak, history, settings, identity.
 * v2.2: credits/refill/ad-bonus system removed per MASTERPIECE-v2.2 §2.3.
 * All persisted to IndexedDB; debounced writeback.
 */
import { type Store, createStore } from './store';

export interface SolveRecord {
  date: string; // UTC YYYY-MM-DD
  timeMs: number; // solve time in ms
  attempts: number;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  mode: string;
  /** Speed percentile (0-1) — fraction of solvers this solve beat. Set async after postSolve. */
  pct?: number;
  /** The player's actual solve path (edge ids, in traversal order) so Review can
   *  replay "my path" vs the canonical solution. Absent on pre-v2.2 records. */
  path?: number[];
}

export interface ProfileState {
  /** Anonymous local user id; minted on first run. */
  uid: string;
  /** Streak in consecutive UTC days. */
  streak: number;
  /** Longest streak ever. */
  bestStreak: number;
  /** UTC date of last solve. */
  lastSolveDate: string | null;
  /** Freeze tokens (earned every Sunday solve). */
  freezeTokens: number;
  /** Cached country code, ISO 3166-1 alpha-2 (uppercase). */
  country: string;
  /** ISO-639 locale chosen by user; null = auto. */
  localeOverride: string | null;
  /** 'light' | 'dark' | 'auto' | unlocked theme id. */
  theme: string;
  /** Solved-puzzle history (newest last; capped to 730 = ~2yr). */
  history: SolveRecord[];
  /** Total solves count (lifetime, including pre-cap). */
  totalSolves: number;
  /** Elo-style rating. Starts at 1000. */
  rating: number;
  /** Onboarding completed? */
  onboarded: boolean;
  /** Notification permission decision recorded? */
  notifAsked: boolean;
  /** Daily streak-reminder local time, 'HH:MM'. Default '08:00'. */
  reminderHour: string;
  /** In-game audio feedback toggle. Default true. */
  soundEnabled: boolean;
  /** v2.3 FDT-O-005 — celebration haptics toggle (Settings). Default true. */
  hapticsEnabled: boolean;
  /** Invite code applied (one-time). */
  inviteUsed: string | null;
  /** Player's own invite code (generated). */
  myInviteCode: string;
  /** Reduced motion / high contrast user pref override (overrides system). */
  a11y: {
    reduceMotion: 'auto' | 'on' | 'off';
    highContrast: 'auto' | 'on' | 'off';
    colorBlind: 'off' | 'protanopia' | 'deuteranopia' | 'tritanopia';
  };
  /** Cookie consent for EU/UK web — null = not asked. */
  cookieConsent: 'accepted' | 'declined' | null;
  /** First seen ISO timestamp. */
  createdAt: string;
  /** OAuth provider bound ('google' | 'apple' | null). */
  oauthProvider: string | null;
  /** Unix timestamp of last OAuth prompt shown (24h cooldown). */
  oauth_prompted_at: number | null;
  /** Fudeto Pro subscription state (v2.2 — no trial). */
  vipState: 'free' | 'active' | 'grace' | 'expired';
  /** v2.3 (I-19) — Euler rule hints (start bubble, dead-end shake, stuck toast,
   *  idle ghost-path). Toggleable; off = v2.2 purist mode. Default true. */
  hintsEnabled: boolean;
  /** v2.3 — first-solve hint bubble shown once ever (then never again). */
  hintBubbleShown: boolean;
  /** v2.3 (FDT-O-001) — welcome puzzle (first-run, totalSolves===0) shown. */
  welcomeShown: boolean;
  /** v2.3 (FDT-O-003) — Practice chapter stars, keyed "chapter-stage" → 1..3. */
  chapterStars: Record<string, number>;
  /** Player's recorded solve path per cleared practice stage (edge ids, traversal
   *  order), keyed like chapterStars, so a cleared stage can replay "my solve". */
  chapterPaths: Record<string, number[]>;
  /** FDT-O-015 — World Cities level stars, keyed "wc-<cityId>-<level>" → 1..3. */
  cityStars: Record<string, number>;
  /** Player's recorded solve path per cleared World Cities level (edge ids,
   *  traversal order), keyed like cityStars, so a cleared city level can replay
   *  "my solve" — same affordance as Daily / Practice review. */
  cityPaths: Record<string, number[]>;
  /** Replay budget per puzzle. After the FIRST clear, a puzzle may be replayed
   *  to improve time/stars up to REDO_LIMIT times; this counts redos used.
   *  Keys: daily "d-<date>", practice "<chapter>-<stage>", city "wc-<id>-<lvl>".
   *  Redos never re-increment totalSolves/streak — they only refine the best. */
  redoCounts: Record<string, number>;
}

/** Per-puzzle replay cap (after the first clear). Owner decision 2026-06-04. */
export const REDO_LIMIT = 3;
/** Redo-budget key for a daily date (namespaced so it can't collide with a
 *  practice "chapter-stage" key, which is also dash-delimited). */
export function dailyRedoKey(date: string): string {
  return `d-${date}`;
}
/** How many redos already spent on a puzzle (0 if never replayed). */
export function redoCountFor(key: string): number {
  return profile().get().redoCounts?.[key] ?? 0;
}
/** Whether a puzzle still has replay budget left (first clear doesn't count). */
export function canRedo(key: string): boolean {
  return redoCountFor(key) < REDO_LIMIT;
}

function newCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  const r = crypto.getRandomValues(new Uint8Array(6));
  for (let i = 0; i < 6; i++) out += alphabet[r[i]! % alphabet.length];
  return `FUDETO-${out}`;
}

function newUid(): string {
  const c = (typeof crypto !== 'undefined' ? crypto : null) as
    | (Crypto & { randomUUID?: () => string })
    | null;
  if (c?.randomUUID) return c.randomUUID();
  if (c) {
    const r = c.getRandomValues(new Uint8Array(16));
    return Array.from(r)
      .map((b: number) => b.toString(16).padStart(2, '0'))
      .join('');
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function detectCountry(): string {
  try {
    const lang = navigator.language;
    const m = /-([A-Z]{2})/.exec(lang);
    if (m?.[1]) return m[1];
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz?.startsWith('Asia/Tokyo')) return 'JP';
    if (tz?.startsWith('Asia/Taipei')) return 'TW';
    if (tz?.startsWith('Asia/Shanghai')) return 'CN';
    if (tz?.startsWith('Asia/Seoul')) return 'KR';
    if (tz?.startsWith('America/')) return 'US';
    if (tz?.startsWith('Europe/')) return 'GB';
  } catch {
    /* fall through */
  }
  return 'US';
}

export function defaultProfile(): ProfileState {
  return {
    uid: newUid(),
    streak: 0,
    bestStreak: 0,
    lastSolveDate: null,
    freezeTokens: 0,
    country: detectCountry(),
    localeOverride: null,
    theme: 'dark',
    history: [],
    totalSolves: 0,
    rating: 1000,
    onboarded: false,
    notifAsked: false,
    reminderHour: '08:00',
    soundEnabled: true,
    hapticsEnabled: true,
    inviteUsed: null,
    myInviteCode: newCode(),
    a11y: { reduceMotion: 'auto', highContrast: 'auto', colorBlind: 'off' },
    cookieConsent: null,
    createdAt: new Date().toISOString(),
    oauthProvider: null,
    oauth_prompted_at: null,
    vipState: 'free',
    hintsEnabled: true,
    hintBubbleShown: false,
    welcomeShown: false,
    chapterStars: {},
    chapterPaths: {},
    cityStars: {},
    cityPaths: {},
    redoCounts: {},
  };
}

const KEY = 'profile.v1';
let _store: Store<ProfileState> | null = null;
let saveTimer: ReturnType<typeof setTimeout> | null = null;

export async function initProfile(): Promise<Store<ProfileState>> {
  if (_store) return _store;
  const persisted = await idbGet<ProfileState>(KEY);
  const initial = persisted ? { ...defaultProfile(), ...persisted } : defaultProfile();
  _store = createStore<ProfileState>(initial);
  _store.subscribe((s) => {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => idbSet(KEY, s), 200);
  });
  // First-run persist so uid + inviteCode are stable.
  if (!persisted) await idbSet(KEY, initial);
  return _store;
}

export function profile(): Store<ProfileState> {
  if (!_store) throw new Error('profile not initialized — call initProfile() in boot');
  return _store;
}

/**
 * Record a daily solve. Returns the current streak.
 *
 * Two distinct paths:
 *  - FIRST solve of a date → counts: streak, bestStreak, totalSolves+1, append
 *    history, Sunday freeze token. (totalSolves therefore == distinct days.)
 *  - REDO of an already-solved date → does NOT touch streak/totalSolves; only
 *    keeps the BEST time (and the faster run's path) and bumps the redo counter.
 *    This is what guarantees "每日已解" can never be inflated by replaying.
 */
export function recordSolve(record: SolveRecord): number {
  const s = profile();
  const cur = s.get();
  const date = record.date;
  const existing = cur.history.find((h) => h.date === date);

  if (existing) {
    // REDO — refine best time + best run's path; never re-count the day.
    const faster = record.timeMs < existing.timeMs;
    const newHistory = cur.history.map((h) =>
      h.date === date
        ? {
            ...h,
            timeMs: Math.min(h.timeMs, record.timeMs),
            ...(faster ? { path: record.path } : {}),
          }
        : h,
    );
    const rk = dailyRedoKey(date);
    s.set({
      history: newHistory,
      redoCounts: { ...(cur.redoCounts ?? {}), [rk]: (cur.redoCounts?.[rk] ?? 0) + 1 },
    });
    return cur.streak;
  }

  // FIRST solve of this date.
  const yesterday = utcDaysAgo(date, 1);
  let newStreak = cur.streak;
  if (cur.lastSolveDate === date) {
    // already counted today (defensive — redo branch handles repeats)
  } else if (cur.lastSolveDate === yesterday || cur.lastSolveDate === null) {
    newStreak = cur.lastSolveDate === yesterday ? cur.streak + 1 : 1;
  } else {
    // missed days. Spend a freeze token if we have one and gap is exactly 1 extra day.
    if (cur.freezeTokens > 0 && utcDaysAgo(date, 2) === cur.lastSolveDate) {
      newStreak = cur.streak + 1;
      s.set({ freezeTokens: cur.freezeTokens - 1 });
    } else {
      newStreak = 1;
    }
  }
  const newHistory = cur.history.concat(record).slice(-730);
  s.set({
    streak: newStreak,
    bestStreak: Math.max(cur.bestStreak, newStreak),
    lastSolveDate: date,
    history: newHistory,
    totalSolves: cur.totalSolves + 1,
  });
  // Sunday solve = +1 freeze token (max 3 banked).
  if (new Date(date + 'T00:00:00Z').getUTCDay() === 0) {
    const fp = profile().get();
    if (fp.freezeTokens < 3) profile().set({ freezeTokens: fp.freezeTokens + 1 });
  }
  return newStreak;
}

/**
 * Backfill the speed percentile onto the most recent solve for `date`.
 * Called after postSolve resolves (pct is computed server-side from rank).
 * No-op if the record can't be found (offline / out-of-order).
 */
export function setSolvePct(date: string, pct: number): void {
  const s = profile();
  const cur = s.get();
  // newest-last: find the last record matching date
  for (let i = cur.history.length - 1; i >= 0; i--) {
    if (cur.history[i]!.date === date) {
      const next = cur.history.slice();
      next[i] = { ...next[i]!, pct };
      s.set({ history: next });
      return;
    }
  }
}

function utcDaysAgo(yyyyMmDd: string, days: number): string {
  const d = new Date(yyyyMmDd + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}
